import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Base de données des utilisateurs (en mémoire pour cet exemple)
// Un seul administrateur avec identifiant et mot de passe unique
let users = [
  {
    id: 1,
    email: 'admin@lafermeancienne.fr',
    password: 'AdminFerme2024!',
    firstName: 'Administrateur',
    lastName: 'Ferme',
    role: 'admin',
    createdAt: new Date().toISOString()
  }
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Vérifier si l'utilisateur est connecté au chargement
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (email, password) => {
    setIsLoading(true);
    
    // Simuler une requête API
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const foundUser = users.find(u => u.email === email && u.password === password);
    
    if (foundUser) {
      const { password, ...userWithoutPassword } = foundUser;
      setUser(userWithoutPassword);
      localStorage.setItem('user', JSON.stringify(userWithoutPassword));
      setIsLoading(false);
      return { success: true, user: userWithoutPassword };
    } else {
      setIsLoading(false);
      return { success: false, error: 'Email ou mot de passe incorrect' };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    setIsLoading(true);
    
    // Simuler une requête API
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Vérifier si l'email existe déjà
    const existingUser = users.find(u => u.email === email);
    if (existingUser) {
      setIsLoading(false);
      return { success: false, error: 'Cet email est déjà utilisé' };
    }
    
    // Créer un nouvel utilisateur
    const newUser = {
      id: users.length + 1,
      email,
      password,
      firstName,
      lastName,
      role: 'client',
      createdAt: new Date().toISOString()
    };
    
    users.push(newUser);
    const { password: _, ...userWithoutPassword } = newUser;
    setUser(userWithoutPassword);
    localStorage.setItem('user', JSON.stringify(userWithoutPassword));
    setIsLoading(false);
    
    return { success: true, user: userWithoutPassword };
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const resetPassword = async (email) => {
    setIsLoading(true);
    
    // Simuler une requête API
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const foundUser = users.find(u => u.email === email);
    setIsLoading(false);
    
    if (foundUser) {
      return { success: true, message: 'Un email de réinitialisation a été envoyé' };
    } else {
      return { success: false, error: 'Email non trouvé' };
    }
  };

  const getAllUsers = () => {
    return users.map(({ password, ...userWithoutPassword }) => userWithoutPassword);
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    resetPassword,
    getAllUsers,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
